import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { Package } from "@/types";

function PackagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [error, setError] = useState("");

  // Load packages on mount and when user changes
  useEffect(() => {
    if (user) {
      loadPackages();
    }
  }, [user]);

  const loadPackages = async () => {
    try {
      setIsLoading(true);
      const API_BASE = import.meta.env.DEV
        ? "http://localhost:8787"
        : "https://shopglobal.finhub.workers.dev";

      const response = await fetch(`${API_BASE}/api/packages`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("shopglobal_auth_token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      } else {
        setError("Failed to load packages");
      }
    } catch (error) {
      console.error("Error loading packages:", error);
      setError("Failed to load packages");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "received":
        return "text-green-600 bg-green-100";
      case "paid":
        return "text-blue-600 bg-blue-100";
      case "shipped":
        return "text-purple-600 bg-purple-100";
      case "delivered":
        return "text-gray-600 bg-gray-100";
      case "incoming":
        return "text-yellow-600 bg-yellow-100";
      case "expected":
        return "text-indigo-600 bg-indigo-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusBadge = (status: string) => {
    const color = getStatusColor(status);
    const readableStatus = status.charAt(0).toUpperCase() + status.slice(1);
    return (
      <span
        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {readableStatus}
      </span>
    );
  };

  // Filter packages based on selected filter
  const filteredPackages = packages.filter((pkg) => {
    if (filter === "all") return true;
    if (filter === "active")
      return ["expected", "incoming", "received"].includes(pkg.status);
    return pkg.status === filter;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="font-bold text-sm">SG</span>
              </div>
              <span className="ml-2 font-bold text-lg">ShopGlobal Pro</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-gray-600 hover:text-gray-900">
                Dashboard
              </Link>
              <Link to="/packages" className="text-blue-600 font-semibold">
                Packages
              </Link>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  {user?.fullName?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="ml-2">{user?.fullName}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Packages</h1>
          <p className="text-gray-600">
            Track and manage all your imported packages
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {packages.length}
              </div>
              <div className="text-sm text-gray-600">Total Packages</div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {packages.filter((p) => p.status === "delivered").length}
              </div>
              <div className="text-sm text-gray-600">Delivered</div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {
                  packages.filter((p) =>
                    ["expected", "incoming", "received"].includes(p.status)
                  ).length
                }
              </div>
              <div className="text-sm text-gray-600">In Process</div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {packages.filter((p) => p.status === "shipped").length}
              </div>
              <div className="text-sm text-gray-600">In Transit</div>
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Filter Packages</h3>
              <div className="flex space-x-2">
                {[
                  { value: "all", label: "All Packages" },
                  { value: "active", label: "Active" },
                  { value: "expected", label: "Expected" },
                  { value: "incoming", label: "Incoming" },
                  { value: "received", label: "Received" },
                  { value: "shipped", label: "Shipped" },
                  { value: "delivered", label: "Delivered" },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      filter === value
                        ? "bg-blue-100 text-blue-700 border-blue-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } border`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Packages List */}
          <div className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
                {error}
              </div>
            )}

            {filteredPackages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <p className="text-lg font-medium mb-2">
                  {filter === "all"
                    ? "No packages yet"
                    : `No ${filter} packages`}
                </p>
                <p className="text-sm">
                  {filter === "all"
                    ? "Register your first package to get started"
                    : `You don't have any ${filter} packages`}
                </p>
                {filter === "all" && (
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700">
                    Register First Package
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusBadge(pkg.status)}
                          <span className="text-sm text-gray-500">
                            ID: {pkg.id}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {pkg.senderName || "Package from US"}
                          {pkg.trackingNumber && (
                            <span className="text-gray-600 font-normal">
                              {" "}
                              • Tracking: {pkg.trackingNumber}
                            </span>
                          )}
                        </h3>

                        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                          <div>
                            <span className="font-medium">Declared Value:</span>{" "}
                            ${pkg.declaredValue} CAD
                          </div>
                          <div>
                            <span className="font-medium">Weight:</span>{" "}
                            {pkg.weight || "Not specified"} lbs
                          </div>
                          <div>
                            <span className="font-medium">Created:</span>{" "}
                            {formatDate(pkg.createdAt)}
                          </div>
                        </div>

                        {pkg.notes && (
                          <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">Notes:</span>{" "}
                            {pkg.notes}
                          </p>
                        )}

                        {/* Package events/timeline */}
                        {pkg.trackingEvents &&
                          pkg.trackingEvents.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Recent Activity
                              </h4>
                              <div className="space-y-1">
                                {pkg.trackingEvents
                                  .slice(0, 3)
                                  .map((event: any, index: number) => (
                                    <div
                                      key={index}
                                      className="text-xs text-gray-600 flex items-center space-x-2">
                                      <div
                                        className={`w-1.5 h-1.5 rounded-full ${
                                          event.status === "delivered"
                                            ? "bg-green-500"
                                            : event.status === "shipped"
                                              ? "bg-purple-500"
                                              : "bg-blue-500"
                                        }`}></div>
                                      <span>
                                        {new Date(
                                          event.createdAt
                                        ).toLocaleDateString()}
                                        : {event.description}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="text-right">
                        <button
                          onClick={() => navigate(`/packages/${pkg.id}`)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                          View Details →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PackagesPage;
